import { useState } from "react";
import { TESTIDS } from "@/constants/testIds";
import { ChevronDown, ChevronRight } from "lucide-react";

function priorityColor(p) {
  if (p === "High") return "pill-gold";
  if (p === "Moderate") return "pill-partial";
  return "pill-neutral";
}

export default function RestOfPlan({ recommendations }) {
  // Section is promoted from a compact collapsible to a full always-visible
  // header; the list itself is expanded by default so the copy's promise of
  // "listed below" is fulfilled on first view. Users can still collapse it.
  const [open, setOpen] = useState(true);
  const rest = (recommendations || []).slice(1);
  if (!rest.length) return null;
  return (
    <section
      className="glass rounded-2xl overflow-hidden"
      data-testid="content-plan-section"
    >
      <div className="px-6 md:px-8 pt-7 md:pt-8 pb-2">
        <div className="label-eyebrow mb-2">Chapter 3 · Your ongoing plan</div>
        <h2
          className="font-serif text-2xl md:text-3xl leading-tight mb-3"
          data-testid="content-plan-headline"
        >
          A complete local-area content plan
        </h2>
        <p
          className="text-[color:var(--text-secondary)] leading-relaxed text-base mb-4 max-w-3xl"
          data-testid="content-plan-intro"
        >
          Once you&apos;ve completed your first FieldGuide, be sure to visit
          your FieldGuide Library, where you&apos;ll find the additional
          FieldGuides listed below. We&apos;ve drafted each especially for your
          property, based on the findings in our Local Authority Report. When
          you fine-tune and publish these, you&apos;ll be well on your way to
          creating a comprehensive, current, experience-based local-area
          resource &mdash; for guests, staff, and AI engines alike.
        </p>
      </div>
      <button
        className="w-full flex items-center justify-between px-6 md:px-8 py-4 text-left border-t"
        style={{ borderColor: "var(--border-default)" }}
        onClick={() => setOpen((v) => !v)}
        data-testid={TESTIDS.restOfPlanToggle}
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          {open ? (
            <ChevronDown className="w-4 h-4" style={{ color: "var(--brand-sage-deep)" }} />
          ) : (
            <ChevronRight className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
          )}
          <div>
            <div className="label-eyebrow mb-1">Your ongoing FieldGuides</div>
            <div className="font-serif text-lg">
              {rest.length} additional FieldGuide{rest.length === 1 ? "" : "s"}, ranked by opportunity
            </div>
          </div>
        </div>
        <div className="text-xs" style={{ color: "var(--text-muted)" }}>
          {open ? "Hide" : "Show"}
        </div>
      </button>
      {open && (
        <div className="px-6 md:px-8 pb-6 space-y-3">
          {rest.map((r) => (
            <div
              key={r.id || r.rank}
              className="p-4 rounded-xl border"
              style={{ borderColor: "var(--border-default)", background: "var(--bg-card-warm)" }}
            >
              <div className="flex items-start gap-4">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: "var(--brand-sage-muted)" }}
                >
                  <span
                    className="font-serif italic text-sm"
                    style={{ color: "var(--brand-sage-deep)" }}
                  >
                    {r.rank}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-lg leading-snug mb-1.5">{r.title}</div>
                  <div className="flex items-center gap-1.5 flex-wrap mb-2">
                    <span className={`pill ${priorityColor(r.priority)}`}>{r.priority}</span>
                    <span className="pill pill-neutral">{r.content_type}</span>
                    <span className="pill pill-neutral">Effort: {r.effort}</span>
                    <span className="pill pill-neutral">Impact: {r.impact}</span>
                  </div>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {r.why_it_matters}
                  </p>
                </div>
              </div>
            </div>
          ))}
          <p className="text-xs italic mt-4" style={{ color: "var(--text-muted)" }}>
            Publish your first FieldGuide before returning to these &mdash; each
            one you fine-tune widens your footprint in the local-area
            conversation.
          </p>
        </div>
      )}
    </section>
  );
}
